/**
 * Crash Logger Utility
 * Tracks all crashes and errors in the application
 * Stores logs in localStorage for later retrieval
 */
import React from "react";

export interface CrashLog {
  id: string;
  timestamp: string;
  type: 'error' | 'warning' | 'info' | 'react-error' | 'promise-rejection' | 'network-error';
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  userAgent?: string;
  appState?: {
    isAuthenticated?: boolean;
    currentScreen?: string;
    userRole?: string;
  };
  additionalData?: any;
}

class CrashLogger {
  private static instance: CrashLogger;
  private maxLogs = 100; // Keep last 100 logs
  private storageKey = 'atootbandan_crash_logs';
  private sessionKey = 'atootbandan_session_id';
  private initialized = false;

  private constructor() {
    this.init();
  }

  public static getInstance(): CrashLogger {
    if (!CrashLogger.instance) {
      CrashLogger.instance = new CrashLogger();
    }
    return CrashLogger.instance;
  }

  private init() {
    if (this.initialized) return;
    
    // Create or get session ID
    if (!sessionStorage.getItem(this.sessionKey)) {
      sessionStorage.setItem(this.sessionKey, this.generateId());
    }

    // Log initialization
    this.log({
      type: 'info',
      message: '🚀 Crash Logger Initialized',
      additionalData: {
        sessionId: this.getSessionId(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      }
    });

    this.initialized = true;
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getSessionId(): string {
    return sessionStorage.getItem(this.sessionKey) || 'unknown';
  }

  /**
   * Get all logs from storage
   */
  public getLogs(): CrashLog[] {
    try {
      const logs = localStorage.getItem(this.storageKey);
      return logs ? JSON.parse(logs) : [];
    } catch (error) {
      console.error('Failed to retrieve crash logs:', error);
      return [];
    }
  }

  /**
   * Save logs to storage
   */
  private saveLogs(logs: CrashLog[]) {
    try {
      // Keep only the last maxLogs entries
      const trimmedLogs = logs.slice(-this.maxLogs);
      localStorage.setItem(this.storageKey, JSON.stringify(trimmedLogs));
    } catch (error) {
      console.error('Failed to save crash logs:', error);
    }
  }

  /**
   * Log a crash or error
   */
  public log(params: {
    type: CrashLog['type'];
    message: string;
    error?: Error;
    stack?: string;
    componentStack?: string;
    additionalData?: any;
  }) {
    const crashLog: CrashLog = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: params.type,
      message: params.message,
      stack: params.stack || params.error?.stack,
      componentStack: params.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      appState: this.captureAppState(),
      additionalData: {
        sessionId: this.getSessionId(),
        ...params.additionalData,
      },
    };

    // Get existing logs
    const logs = this.getLogs();
    logs.push(crashLog);
    this.saveLogs(logs);

    // Console output for development - skip network errors in demo mode
    const isDemoMode = localStorage.getItem('demoMode') === 'true' || !localStorage.getItem('token');
    const isNetworkError = params.type === 'network-error' || params.message.includes('Failed to fetch') || params.message.includes('Network Error');
    
    if (isNetworkError && isDemoMode) {
      // Silently handle network errors in demo mode - only store, don't log
      return crashLog;
    }
    
    if (params.type === 'error' || params.type === 'react-error') {
      console.error('🔴 CRASH LOGGED:', crashLog);
    } else if (params.type === 'warning') {
      if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
        console.warn('⚠️ WARNING LOGGED:', crashLog);
      }
    } else {
      if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
        console.log('ℹ️ INFO LOGGED:', crashLog);
      }
    }

    return crashLog;
  }

  /**
   * Log a React error
   */
  public logReactError(error: Error, errorInfo: React.ErrorInfo) {
    return this.log({
      type: 'react-error',
      message: `React Error: ${error.message}`,
      error,
      componentStack: errorInfo.componentStack,
      additionalData: {
        errorInfo,
      },
    });
  }

  /**
   * Log a JavaScript error
   */
  public logJavaScriptError(error: Error | string, source?: string, lineno?: number, colno?: number) {
    const message = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'object' ? error.stack : undefined;

    return this.log({
      type: 'error',
      message: `JavaScript Error: ${message}`,
      stack,
      additionalData: {
        source,
        lineno,
        colno,
      },
    });
  }

  /**
   * Log a promise rejection
   */
  public logPromiseRejection(reason: any, promise: Promise<any>) {
    const message = reason?.message || reason?.toString() || 'Unknown promise rejection';
    
    // Skip logging "Failed to fetch" errors in demo mode (these are expected when backend is offline)
    const isDemoMode = localStorage.getItem('demoMode') === 'true' || !localStorage.getItem('token');
    const isNetworkError = message.includes('Failed to fetch') || message.includes('Network Error') || message.includes('NetworkError');
    
    if (isNetworkError && isDemoMode) {
      // Silently handle network-related promise rejections in demo mode
      return null;
    }
    
    return this.log({
      type: 'promise-rejection',
      message: `Unhandled Promise Rejection: ${message}`,
      stack: reason?.stack,
      additionalData: {
        reason,
      },
    });
  }

  /**
   * Log a network error
   */
  public logNetworkError(error: any, url?: string, method?: string) {
    return this.log({
      type: 'network-error',
      message: `Network Error: ${error.message || 'Request failed'}`,
      stack: error.stack,
      additionalData: {
        url,
        method,
        status: error.response?.status,
        statusText: error.response?.statusText,
      },
    });
  }

  /**
   * Log a warning
   */
  public logWarning(message: string, additionalData?: any) {
    return this.log({
      type: 'warning',
      message,
      additionalData,
    });
  }

  /**
   * Log info
   */
  public logInfo(message: string, additionalData?: any) {
    return this.log({
      type: 'info',
      message,
      additionalData,
    });
  }

  /**
   * Capture current app state
   */
  private captureAppState(): CrashLog['appState'] {
    try {
      const token = localStorage.getItem('token');
      const demoMode = localStorage.getItem('demoMode');
      
      return {
        isAuthenticated: !!token,
        currentScreen: window.location.pathname,
        userRole: demoMode === 'true' ? 'demo-user' : 'unknown',
      };
    } catch (error) {
      return {};
    }
  }

  /**
   * Clear all logs
   */
  public clearLogs() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('✅ Crash logs cleared');
    } catch (error) {
      console.error('Failed to clear crash logs:', error);
    }
  }

  /**
   * Export logs as JSON
   */
  public exportLogs(): string {
    const logs = this.getLogs();
    return JSON.stringify(logs, null, 2);
  }

  /**
   * Download logs as a file
   */
  public downloadLogs() {
    const logs = this.exportLogs();
    const blob = new Blob([logs], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `atootbandan-crash-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('✅ Crash logs downloaded');
  }

  /**
   * Get logs count
   */
  public getLogsCount(): number {
    return this.getLogs().length;
  }

  /**
   * Get error logs only
   */
  public getErrorLogs(): CrashLog[] {
    return this.getLogs().filter(log => 
      log.type === 'error' || 
      log.type === 'react-error' || 
      log.type === 'promise-rejection' ||
      log.type === 'network-error'
    );
  }

  /**
   * Get logs by type
   */
  public getLogsByType(type: CrashLog['type']): CrashLog[] {
    return this.getLogs().filter(log => log.type === type);
  }

  /**
   * Get recent logs (last n logs)
   */
  public getRecentLogs(count: number = 10): CrashLog[] {
    const logs = this.getLogs();
    return logs.slice(-count);
  }
}

// Export singleton instance
export const crashLogger = CrashLogger.getInstance();

// Setup global error handlers
export function setupGlobalErrorHandlers() {
  // Handle uncaught JavaScript errors
  window.addEventListener('error', (event) => {
    crashLogger.logJavaScriptError(
      event.error || event.message,
      event.filename,
      event.lineno,
      event.colno
    );
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    crashLogger.logPromiseRejection(event.reason, event.promise);
    event.preventDefault(); // Prevent default browser error
  });

  // Log when page is being unloaded (potential crash)
  window.addEventListener('beforeunload', () => {
    crashLogger.logInfo('Page unload detected', {
      timestamp: new Date().toISOString(),
    });
  });

  console.log('✅ Global error handlers installed');
}

export default crashLogger;